import { useState, useCallback, useRef, useLayoutEffect, useMemo, useEffect } from 'react';
import { screenToCanvas as screenToCanvasUtil } from './utils/canvasUtils';
import { useFlowchartZoom } from './useFlowchartZoom';
import { useFlowchartPan } from './useFlowchartPan';
import { useFlowchartSelection } from './useFlowchartSelection';
import { useFlowchartConnection } from './useFlowchartConnection';
import { useFlowchartDrag } from './useFlowchartDrag';
import { useViewportConstraints } from './useViewportConstraints';
import { useNestDrop } from './useNestDrop';
import { useFlowchartEvents } from './useFlowchartEvents';

/**
 * useFlowchartInteraction Hook
 * 
 * Composes smaller focused hooks for flowchart interactions:
 * - Selection, panning, zooming, dragging, connections, nesting
 * - Event orchestration via useFlowchartEvents
 */
export function useFlowchartInteraction({
  tasks,
  nodePositions,
  nodeDimensions = {},
  onDependencyChange,
  onConditionsChange,
  onPositionChange,
  onPositionChangeBatch,
  onDeleteTasks,
  snapToGrid = false,
  autoArrange = false,
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
  isMultiCampaignMode = false, // When true, ignore saved positions and use auto-layout
  effectivePositionsRef = null, // Ref to positions used for rendering (for focus mode selection box)
  onConnectionComplete, // Called after a connection is created (sourceId, targetId) - for auto-tidy
  onEdgeDeleted = null, // Called after a connection line is deleted — same auto-tidy
  onAfterDelete = null, // Called after nodes are deleted (for auto-pan to nearest visible node)
  onCanvasContextMenu = null, // Called when right-clicking on empty canvas space
  onDismissCanvasContextMenu = null, // Hides that menu when the press turns into a drag
  campaignId = '',
  campaignName = '',
  // Filter/FOKUS mode integration
  hasActiveFilters = false, // True when filters are active
  onChainTouched = null, // Callback when a chain is modified (for re-layout on mode exit)
}) {
  const containerRef = useRef(null);
  const disableDragRef = useRef(false);
  const isFocusModeRef = useRef(false);
  const isPanningRef = useRef(false); // Shared ref to track if user is panning (for zoom/pan coordination)
  
  // ═══════════════════════════════════════════════════════════════
  // SCROLL PREVENTION
  // ═══════════════════════════════════════════════════════════════
  // 
  // CRITICAL FIX: Prevent the container from scrolling when elements inside get focused.
  // The flowchart uses CSS transforms for panning, not scrolling. But when an input
  // element (like a task name) gets focused, the browser auto-scrolls to make it visible.
  // If that element is at x=10000+, the container scrolls way over, pushing the HUD
  // and everything else off screen. This effect resets scroll to 0 whenever it changes.
  //
  // Also prevents scroll when pasting nodes that extend beyond the viewport - the browser
  // may try to auto-scroll to show newly added DOM elements.
  //
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    let rafId = null;
    let rafLoopActive = false;
    
    const preventScroll = () => {
      if (container.scrollLeft !== 0 || container.scrollTop !== 0) {
        container.scrollLeft = 0;
        container.scrollTop = 0;
      }
    };
    
    // Aggressive RAF loop - runs for a short burst after DOM changes
    const startRafLoop = () => {
      if (rafLoopActive) return;
      rafLoopActive = true;
      let frameCount = 0;
      const maxFrames = 10; // Run for ~10 frames (~166ms at 60fps)
      
      const loop = () => {
        preventScroll();
        frameCount++;
        if (frameCount < maxFrames) {
          rafId = requestAnimationFrame(loop);
        } else {
          rafLoopActive = false;
        }
      };
      rafId = requestAnimationFrame(loop);
    };
    
    // Watch for DOM changes (new nodes being added)
    const observer = new MutationObserver(() => {
      startRafLoop();
    });
    
    observer.observe(container, { 
      childList: true, 
      subtree: true 
    });
    
    // Also listen for scroll events as backup
    container.addEventListener('scroll', preventScroll, { passive: true });
    
    // Initial check
    preventScroll();
    
    return () => {
      container.removeEventListener('scroll', preventScroll);
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);
  
  // ═══════════════════════════════════════════════════════════════
  // POSITION STATE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════
  
  const savedPositions = useMemo(() => {
    // In multi-campaign mode, positions are handled by useFlowchartLayout
    // which applies horizontal offsets. We don't want to override those here.
    if (isMultiCampaignMode) {
      return {};
    }
    
    // Single campaign mode: restore positions from task data
    const positions = {};
    tasks.forEach(task => {
      if (task.x !== undefined && task.y !== undefined) {
        positions[task.id] = { x: task.x, y: task.y };
      }
    });
    return positions;
  }, [tasks, isMultiCampaignMode]);
  
  const [customPositions, setCustomPositionsState] = useState({});
  const customPositionsRef = useRef({});
  
  // Track previous savedPositions to detect actual changes to task.x/y
  // This prevents infinite loops while still allowing undo to restore positions
  const prevSavedPositionsRef = useRef({});
  
  useLayoutEffect(() => {
    if (Object.keys(savedPositions).length > 0) {
      const currentCustom = customPositionsRef.current;
      const prevSaved = prevSavedPositionsRef.current;
      let needsUpdate = false;
      const merged = { ...currentCustom };
      
      Object.entries(savedPositions).forEach(([taskId, pos]) => {
        const existing = merged[taskId];
        const prevPos = prevSaved[taskId];
        
        // Check if the SAVED position has changed from what it was before
        // This happens when: undo restores original x/y, or external edit changes x/y
        const savedPositionChanged = !prevPos || prevPos.x !== pos.x || prevPos.y !== pos.y;
        
        // Only pull server/saved coords into custom when custom still matched the *previous*
        // saved snapshot. Otherwise a stale `tasks` render (old x/y) can clobber a fresh drag
        // before the parent applies the new position.
        if (!existing) {
          merged[taskId] = pos;
          needsUpdate = true;
        } else if (
          prevPos &&
          savedPositionChanged &&
          (existing.x !== pos.x || existing.y !== pos.y)
        ) {
          const customMatchedPrevSaved =
            Math.abs(existing.x - prevPos.x) <= 0.5 && Math.abs(existing.y - prevPos.y) <= 0.5;
          if (customMatchedPrevSaved) {
            merged[taskId] = pos;
            needsUpdate = true;
          }
        }
      });
      
      // Always update the ref to track what we've seen
      prevSavedPositionsRef.current = { ...savedPositions };
      
      if (needsUpdate) {
        customPositionsRef.current = merged;
        setCustomPositionsState(merged);
      }
    }
  }, [savedPositions]);
  
  const setCustomPositions = useCallback((updater) => {
    if (typeof updater === 'function') {
      const newValue = updater(customPositionsRef.current);
      customPositionsRef.current = newValue;
      setCustomPositionsState(newValue);
    } else {
      customPositionsRef.current = updater;
      setCustomPositionsState(updater);
    }
  }, []);
  
  // ═══════════════════════════════════════════════════════════════
  // HOOK INITIALIZATION
  // ═══════════════════════════════════════════════════════════════
  
  // Pass effectivePositionsRef to panHook so it can use FOKUS mode positions for calculations
  const panHook = useFlowchartPan(containerRef, nodePositions, customPositions, tasks, nodeDimensions, effectivePositionsRef);
  const { pan, isPanning } = panHook;
  
  const zoomHook = useFlowchartZoom(containerRef, pan, panHook.setPan);
  
  const viewportConstraints = useViewportConstraints({
    containerRef, tasks, nodePositions, customPositions, nodeDimensions,
    panHook, zoomHook, isFocusModeRef, isPanningRef,
    trackpadPanSensitivity, trackpadZoomSensitivity,
  });
  const {
    zoom, zoomRef, setZoom, setPan, setPanAnimated, setPanUnconstrained, contentBounds, containerRectRef,
    constrainPan, handleWheel, handleZoomIn, handleZoomOut, cancelPanCorrection, handleResetView, handleGoToStart, handleGoToStartInstant, skipResizeAdjustment,
    isAnimatingPan,
  } = viewportConstraints;

  const screenToCanvas = useCallback((screenX, screenY) => {
    return screenToCanvasUtil(screenX, screenY, containerRef, pan, zoom);
  }, [pan, zoom]);

  const selectionHook = useFlowchartSelection({
    tasks, nodePositions, customPositions, nodeDimensions,
    screenToCanvas, onConditionsChange, onDependencyChange, onDeleteTasks,
    effectivePositionsRef, // Pass through for focus mode selection box
    onAfterDelete, // Pass through for auto-pan after deletion
    onEdgeDeleted,
    campaignId,
    campaignName,
  });
  const {
    selectedNode, selectedNodes, setSelectedNodes, selectedEdge, setSelectedEdge,
    selectionBox, isSelectingBox, startSelectionBox, updateSelectionBox, completeSelectionBox,
    selectNode, handleEdgeClick, clearSelection, deleteSelectedNodes,
  } = selectionHook;

  // Compute if we're in a temporary mode (filter or FOKUS)
  // Note: This is now only used to mark chains as "touched" for re-layout on mode exit
  // Position saves and undo recording happen regardless of mode
  const getIsInTemporaryMode = useCallback(() => {
    return hasActiveFilters || (isFocusModeRef?.current === true);
  }, [hasActiveFilters, isFocusModeRef]);

  const dragHook = useFlowchartDrag({
    tasks, nodePositions, nodeDimensions, selectedNodes, screenToCanvas,
    snapToGrid, autoArrange, disableDragRef, onPositionChange, onPositionChangeBatch,
    customPositions, setCustomPositions,
    isMultiCampaignMode,
    campaignId,
    getIsInTemporaryMode, // Function to check at drag completion time
    onChainTouched,
  });
  const {
    draggingNode, draggingNodeRef, isDragging, handleNodeDragStart, checkDragThreshold,
    updateDragPosition, completeDrag, handleAutoLayout: dragAutoLayout,
    autoArrangeBlocked, dropTarget, potentialDragNode,
    dragLayoutTick, currentDragPositionsRef,
  } = dragHook;
  
  const nestDropHook = useNestDrop({
    tasks, nodePositions, nodeDimensions, draggingNode, selectedNodes,
  });
  const { pendingNestAction, getDependencyInfo, cancelNestAction, confirmNestAction: nestConfirmAction, initiateNestAction } = nestDropHook;
  
  const handleAutoLayout = useCallback((overrideSelectedNodes = null) => {
    dragAutoLayout(overrideSelectedNodes || selectedNodes);
  }, [dragAutoLayout, selectedNodes]);

  const connectionHook = useFlowchartConnection({
    tasks, nodePositions, nodeDimensions, customPositions, onConditionsChange, onDependencyChange, clearSelection,
    onConnectionComplete,
  });
  const {
    connectingFrom, connectingTarget, connectingBranch, connectingStartPos, reconnectingEdge, isConnecting,
    handleConnectionDragStart, handleEdgeArrowDragStart, handleConnectionTargetEnter, handleConnectionTargetLeave,
    updateConnectionTarget, completeConnection, completeConnectionIfValid, cancelConnection,
  } = connectionHook;

  // ═══════════════════════════════════════════════════════════════
  // EMPTY CANVAS AUTO-CENTER
  // ═══════════════════════════════════════════════════════════════
  
  // Track previous task count to detect when canvas becomes empty
  const prevTaskCountRef = useRef(tasks.length);
  
  // Auto-center canvas when all tasks are deleted
  // This ensures the "No missions yet" message appears centered
  useEffect(() => {
    const prevCount = prevTaskCountRef.current;
    const currentCount = tasks.length;
    prevTaskCountRef.current = currentCount;
    
    // Only trigger when going from having tasks to having none
    if (prevCount > 0 && currentCount === 0) {
      // Reset to center position with smooth animation
      setPanAnimated({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [tasks.length, setPanAnimated, setZoom]);

  // ═══════════════════════════════════════════════════════════════
  // EVENT ORCHESTRATION
  // ═══════════════════════════════════════════════════════════════

  const eventsHook = useFlowchartEvents({
    containerRef, screenToCanvas,
    panHook, setPan, setPanAnimated, constrainPan, cancelPanCorrection, isPanningRef, zoomRef, // Pass zoomRef for direct DOM updates (not zoom state which may be stale)
    startSelectionBox, updateSelectionBox, completeSelectionBox, selectNode,
    setSelectedNodes, setSelectedEdge, clearSelection, isSelectingBox,
    potentialDragNode, checkDragThreshold, updateDragPosition, completeDrag, isDragging,
    draggingNodeRef,
    isConnecting, connectingFrom, updateConnectionTarget,
    completeConnection, completeConnectionIfValid, cancelConnection,
    initiateNestAction, getDependencyInfo,
    onCanvasContextMenu,
    onDismissCanvasContextMenu,
  });
  const {
    mousePos, immediateNestAction, handleMouseMove, handleCanvasMouseDown,
    handleMouseUp, handleContextMenu, handleCanvasClick, clearImmediateNestAction,
  } = eventsHook;

  // ═══════════════════════════════════════════════════════════════
  // WRAPPER HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleNodeDragStartWrapper = useCallback((taskId, e) => {
    const canvasPos = screenToCanvas(e.clientX, e.clientY);
    handleNodeDragStart(taskId, e, canvasPos);
    setSelectedEdge(null);
  }, [handleNodeDragStart, setSelectedEdge, screenToCanvas]);

  const handleNodeClick = useCallback((taskId, e) => {
    e.stopPropagation();
  }, []);

  const confirmNestAction = useCallback((strategy) => {
    return nestConfirmAction(strategy);
  }, [nestConfirmAction]);

  // ═══════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════

  return {
    // Refs
    containerRef, customPositionsRef, disableDragRef, isFocusModeRef, containerRectRef,
    
    // State
    selectedNode, selectedNodes, selectedEdge, customPositions,
    connectingFrom, connectingTarget, connectingBranch, connectingStartPos, reconnectingEdge,
    mousePos, draggingNode, pan, zoom, isPanning, selectionBox, contentBounds,
    
    // Nest/drop state
    dropTarget, pendingNestAction, immediateNestAction,
    
    // State setters
    setPan, setPanAnimated, setPanUnconstrained, setZoom, setSelectedNodes, setCustomPositions,
    
    // Constraint function (for minimap)
    constrainPan,
    
    // Derived state
    isDragging, isConnecting, isSelectingBox,
    isAnimatingPan, // True during camera pan animations (blocks interactions)
    dragLayoutTick,
    currentDragPositionsRef,
    
    // Handlers
    handleMouseMove, handleCanvasMouseDown, handleMouseUp, handleWheel,
    handleContextMenu, handleNodeDragStart: handleNodeDragStartWrapper, handleNodeClick,
    handleConnectionDragStart, handleEdgeArrowDragStart, handleConnectionTargetEnter, handleConnectionTargetLeave,
    handleEdgeClick, handleCanvasClick, handleResetView, handleGoToStart, handleGoToStartInstant,
    handleAutoLayout, handleZoomIn, handleZoomOut, deleteSelectedNodes, skipResizeAdjustment,
    
    // Nest actions
    cancelNestAction, confirmNestAction, clearImmediateNestAction,
    
    // Auto-arrange state
    autoArrangeBlocked,
  };
}
 